import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Mic2, Heart, Play } from "lucide-react";

const AddToQueueDialog = ({ open, onOpenChange, song, onConfirm, defaultSinger, defaultDedication, mode = "queue" }) => {
  const [singer, setSinger] = useState("");
  const [dedication, setDedication] = useState("");
  const isPlayNow = mode === "play_now";

  useEffect(() => {
    if (open) {
      const lastSinger = defaultSinger || localStorage.getItem("last_singer") || "";
      setSinger(lastSinger);
      setDedication(defaultDedication || "");
    }
  }, [open, defaultSinger, defaultDedication]);

  const handleConfirm = () => {
    const name = singer.trim() || "Invitado";
    localStorage.setItem("last_singer", name);
    onConfirm(name, dedication.trim() || null);
    onOpenChange(false);
  };

  if (!song) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface border-border max-w-md font-sans"
        data-testid="add-to-queue-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            {isPlayNow ? <Play className="w-5 h-5 text-secondary" fill="currentColor" /> : <Mic2 className="w-5 h-5 text-primary" />}
            {isPlayNow ? "Reproducir ahora" : "Agregar a la cola"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isPlayNow ? "Esta canción sonará de inmediato. ¿Quién la va a cantar?" : "¿Quién va a cantar esta canción?"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 my-2 p-3 rounded-md bg-background border border-border">
          <img src={song.thumbnail} alt="" className="w-20 h-12 object-cover rounded-sm shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold line-clamp-2 leading-tight">{song.title}</p>
            <p className="text-xs text-muted-foreground mt-1 truncate">{song.channelTitle}</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="singer-name" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Nombre del cantante
          </Label>
          <Input
            id="singer-name"
            value={singer}
            onChange={(e) => setSinger(e.target.value)}
            placeholder="Ej. María, Carlos, Equipo Rojo..."
            data-testid="singer-name-input"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            className="bg-background border-border focus-visible:ring-primary font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="dedication" className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Heart className="w-3 h-3 text-primary" />
            Dedicatoria <span className="text-muted-foreground/60 normal-case font-sans">(opcional)</span>
          </Label>
          <Input
            id="dedication"
            value={dedication}
            onChange={(e) => setDedication(e.target.value)}
            placeholder="Ej. Para mi mamá en su cumpleaños"
            maxLength={120}
            data-testid="dedication-input"
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            className="bg-background border-border focus-visible:ring-primary"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-add-button"
            className="border-border hover:border-secondary hover:text-secondary"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            data-testid="confirm-add-to-queue-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wide"
          >
            {isPlayNow ? "Reproducir ahora" : "Agregar a Cola"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToQueueDialog;
